/**
 * Aircraft Editor Store - Zustand state management for aircraft interaction in ramp view
 *
 * Manages:
 * - Aircraft selection and hover state
 * - Drag and rotation state
 * - Dirty tracking for save/revert functionality
 * - Map center for coordinate calculations
 * - Live detection tracking (area/unit detection during drag)
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { PositionType } from '@/types/allocations'
import type { HangarDetectionResult } from '@/lib/utils/hangar-detection'

/** Edited allocation tracking - stores original and current position for dirty detection */
export interface EditedAllocation {
  allocationId: string
  aircraftId: string
  tailNumber: string
  originalPosition: [number, number, number]  // [x, y, z] in feet
  originalRotation: number                     // degrees
  currentPosition: [number, number, number]    // [x, y, z] in feet
  currentRotation: number                      // degrees
  // Original detection state (captured at drag start)
  originalAreaId: string | null
  originalUnitId: string | null
  originalPositionType: PositionType
}

/** Store state interface */
interface AircraftEditorState {
  // Selection state
  selectedAllocationId: string | null
  hoveredAllocationId: string | null

  // Interaction state
  isDragging: boolean
  isRotating: boolean

  // Map center for coordinate calculations (set once on load)
  mapCenter: { lat: number; lng: number } | null

  // Dirty tracking - allocations that have been modified
  editedAllocations: Map<string, EditedAllocation>

  // Live detection tracking - current area/unit during drag
  currentDetections: Map<string, HangarDetectionResult>

  // UI state
  showInfoPopup: boolean
  showEditForm: boolean

  // Drag lock — aircraft are locked by default; explicit unlock required
  isDragDropEnabled: boolean          // false by default — global free-drag toggle
  cursorFollowTargetId: string | null // when set, this aircraft follows cursor until click
}

/** Store actions interface */
interface AircraftEditorActions {
  // Selection
  selectAllocation: (allocationId: string | null) => void
  setHovered: (allocationId: string | null) => void

  // Interaction
  setDragging: (isDragging: boolean) => void
  setRotating: (isRotating: boolean) => void

  // Map center
  setMapCenter: (center: { lat: number; lng: number }) => void

  // Editing tracking
  startEditing: (
    allocationId: string,
    aircraftId: string,
    tailNumber: string,
    position: [number, number, number],
    rotation: number,
    initialDetection: HangarDetectionResult
  ) => void
  updatePosition: (allocationId: string, position: [number, number, number]) => void
  updateRotation: (allocationId: string, rotation: number) => void
  updateDetection: (allocationId: string, detection: HangarDetectionResult) => void
  markClean: (allocationId: string) => void
  revertAllocation: (allocationId: string) => EditedAllocation | null
  clearEditing: (allocationId: string) => void

  // UI
  setShowInfoPopup: (show: boolean) => void
  setShowEditForm: (show: boolean) => void

  // Drag lock
  setDragDropEnabled: (enabled: boolean) => void
  startCursorFollow: (allocationId: string) => void
  cancelCursorFollow: () => void

  // Helpers
  isDirty: (allocationId: string) => boolean
  getEditedAllocation: (allocationId: string) => EditedAllocation | undefined

  // Reset
  reset: () => void
  /** Targeted reset for location switches — clears location-scoped state, preserves global prefs */
  resetForLocationSwitch: () => void
}

const initialState: AircraftEditorState = {
  selectedAllocationId: null,
  hoveredAllocationId: null,
  isDragging: false,
  isRotating: false,
  mapCenter: null,
  editedAllocations: new Map(),
  currentDetections: new Map(),
  showInfoPopup: false,
  showEditForm: false,
  isDragDropEnabled: false,
  cursorFollowTargetId: null,
}

export const useAircraftEditorStore = create<AircraftEditorState & AircraftEditorActions>()(
  devtools(
    persist(
    (set, get) => ({
      ...initialState,

      // Selection
      selectAllocation: (allocationId) => {
        const updates: Partial<AircraftEditorState> = {
          selectedAllocationId: allocationId,
          // Close popups when selection changes
          showInfoPopup: false,
          showEditForm: false,
        }
        // Cancel cursor-follow when deselecting
        if (allocationId === null && get().cursorFollowTargetId) {
          updates.cursorFollowTargetId = null
          updates.isDragging = false
        }
        set(updates)
      },

      setHovered: (allocationId) => {
        set({ hoveredAllocationId: allocationId })
      },

      // Interaction
      setDragging: (isDragging) => {
        set({ isDragging })
      },

      setRotating: (isRotating) => {
        set({ isRotating })
      },

      // Map center — always update (location switches need to overwrite)
      setMapCenter: (center) => {
        set({ mapCenter: center })
      },

      // Editing tracking
      startEditing: (allocationId, aircraftId, tailNumber, position, rotation, initialDetection) => {
        const { editedAllocations, currentDetections } = get()

        // Only add if not already tracking
        if (!editedAllocations.has(allocationId)) {
          const newAllocationsMap = new Map(editedAllocations)
          newAllocationsMap.set(allocationId, {
            allocationId,
            aircraftId,
            tailNumber,
            originalPosition: [...position] as [number, number, number],
            originalRotation: rotation,
            currentPosition: [...position] as [number, number, number],
            currentRotation: rotation,
            // Store original detection state
            originalAreaId: initialDetection.areaId,
            originalUnitId: initialDetection.unitId,
            originalPositionType: initialDetection.positionType,
          })

          // Initialize current detection with original
          const newDetectionsMap = new Map(currentDetections)
          newDetectionsMap.set(allocationId, initialDetection)

          set({
            editedAllocations: newAllocationsMap,
            currentDetections: newDetectionsMap,
          })
        }
      },

      updatePosition: (allocationId, position) => {
        const { editedAllocations } = get()
        const allocation = editedAllocations.get(allocationId)

        if (allocation) {
          const newMap = new Map(editedAllocations)
          newMap.set(allocationId, {
            ...allocation,
            currentPosition: [...position] as [number, number, number],
          })
          set({ editedAllocations: newMap })
        }
      },

      updateRotation: (allocationId, rotation) => {
        const { editedAllocations } = get()
        const allocation = editedAllocations.get(allocationId)

        if (allocation) {
          const newMap = new Map(editedAllocations)
          newMap.set(allocationId, {
            ...allocation,
            currentRotation: rotation,
          })
          set({ editedAllocations: newMap })
        }
      },

      updateDetection: (allocationId, detection) => {
        const { currentDetections } = get()
        const newMap = new Map(currentDetections)
        newMap.set(allocationId, detection)
        set({ currentDetections: newMap })
      },

      markClean: (allocationId) => {
        // After saving, update the original position to current (no longer dirty)
        const { editedAllocations } = get()
        const allocation = editedAllocations.get(allocationId)

        if (allocation) {
          const newMap = new Map(editedAllocations)
          newMap.set(allocationId, {
            ...allocation,
            originalPosition: [...allocation.currentPosition] as [number, number, number],
            originalRotation: allocation.currentRotation,
          })
          set({ editedAllocations: newMap })
        }
      },

      revertAllocation: (allocationId) => {
        const { editedAllocations } = get()
        const allocation = editedAllocations.get(allocationId)

        if (allocation) {
          const newMap = new Map(editedAllocations)
          // Reset current to original
          newMap.set(allocationId, {
            ...allocation,
            currentPosition: [...allocation.originalPosition] as [number, number, number],
            currentRotation: allocation.originalRotation,
          })
          set({ editedAllocations: newMap })
          return allocation  // Return allocation so caller can update 3D model
        }
        return null
      },

      clearEditing: (allocationId) => {
        const { editedAllocations } = get()
        if (editedAllocations.has(allocationId)) {
          const newMap = new Map(editedAllocations)
          newMap.delete(allocationId)
          set({ editedAllocations: newMap })
        }
      },

      // UI
      setShowInfoPopup: (show) => {
        set({ showInfoPopup: show, showEditForm: false })
      },

      setShowEditForm: (show) => {
        set({ showEditForm: show, showInfoPopup: false })
      },

      // Drag lock
      setDragDropEnabled: (enabled) => {
        set({ isDragDropEnabled: enabled })
      },

      startCursorFollow: (allocationId) => {
        set({
          cursorFollowTargetId: allocationId,
          isDragging: true,
        })
      },

      cancelCursorFollow: () => {
        set({
          cursorFollowTargetId: null,
          isDragging: false,
        })
      },

      // Helpers
      isDirty: (allocationId) => {
        const allocation = get().editedAllocations.get(allocationId)
        if (!allocation) return false

        const posChanged =
          allocation.currentPosition[0] !== allocation.originalPosition[0] ||
          allocation.currentPosition[1] !== allocation.originalPosition[1] ||
          allocation.currentPosition[2] !== allocation.originalPosition[2]

        const rotChanged = allocation.currentRotation !== allocation.originalRotation

        return posChanged || rotChanged
      },

      getEditedAllocation: (allocationId) => {
        return get().editedAllocations.get(allocationId)
      },

      // Reset
      reset: () => {
        set(initialState)
      },

      // Targeted reset for location switches
      // Clears selection, interaction, and edit state but preserves mapCenter and isDragDropEnabled
      // (mapCenter will be updated by setMapCenter immediately after)
      resetForLocationSwitch: () => {
        set({
          selectedAllocationId: null,
          hoveredAllocationId: null,
          isDragging: false,
          isRotating: false,
          editedAllocations: new Map(),
          currentDetections: new Map(),
          showInfoPopup: false,
          showEditForm: false,
          cursorFollowTargetId: null,
          // isDragDropEnabled preserved — it's a global user preference
        })
      },
    }),
      {
        name: 'aircraft-editor-prefs',
        partialize: (state) => ({
          isDragDropEnabled: state.isDragDropEnabled,
        }),
      }
    ),
    { name: 'aircraft-editor-store' }
  )
)
